import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../../risikoanalyseStep/tokens";
import { KLASSEN_LABELS, VEHICLE_CLASS_TO_KEY } from "../../kfzContractStep/data";
import VereinbarungTable from "../../kfzContractStep/VereinbarungTable";
import UmfangAssignmentTable from "../../kfzContractStep/UmfangAssignmentTable";
import type { SammelGridState } from "../../kfzContractStep/SammelvertragGrid";
import SammelvertragZahlungSummary from "../../kfzContractStep/SammelvertragZahlungSummary";
import type { Vereinbarung } from "../../kfzContractStep/vereinbarungMock";
import type {
  KlasseKey,
  RiskSummary,
  TarifierungKey,
} from "../../kfzContractStep/types";

const useStyles = makeStyles({
  empty: {
    paddingTop: "12px",
    paddingBottom: "12px",
    color: BRAND.muted,
    fontStyle: "italic",
    fontSize: "0.92rem",
  },
  werteRoot: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  werteCard: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  werteCardTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    color: BRAND.ink,
    fontWeight: 600,
    margin: 0,
  },
  kvList: {
    display: "grid",
    gridTemplateColumns: "1fr",
    columnGap: "16px",
    rowGap: "4px",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  kvRow: {
    display: "grid",
    gridTemplateColumns: "minmax(180px, 0.7fr) 1.3fr",
    columnGap: "12px",
    alignItems: "baseline",
    paddingTop: "4px",
    paddingBottom: "4px",
    ...shorthands.borderBottom("1px", "dashed", BRAND.borderDashed),
  },
  kvKey: {
    fontSize: "0.78rem",
    fontWeight: 700,
    color: BRAND.muted,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  kvVal: {
    fontSize: "0.92rem",
    color: BRAND.ink,
    whiteSpace: "pre-wrap",
  },
  kvValMuted: {
    color: BRAND.muted,
    fontStyle: "italic",
  },
});

const WERT_FIELD_LABELS: Array<{ key: string; label: string }> = [
  { key: "vehicleValue", label: "Fahrzeugwert (Neu-/Zeitwert)" },
  { key: "labelingValue", label: "Beschriftung" },
  { key: "installationsValue", label: "Einbauten" },
  { key: "bodyValue", label: "Aufbauten" },
  { key: "marketValue", label: "Marktwert (Gutachten)" },
  { key: "marketValueDate", label: "Marktwert — Gutachten vom" },
  { key: "restorationValue", label: "Wiederaufbauwert (Gutachten)" },
  { key: "restorationValueDate", label: "Wiederaufbau — Gutachten vom" },
  { key: "purchasePrice", label: "Listen- bzw. Kaufpreis" },
  { key: "hirePurchasePayment", label: "Mietkauf — Zahlung" },
  { key: "leasingRate", label: "Leasing — Rate" },
];

const ZAHLUNG_FIELD_LABELS: Array<{ key: string; label: string }> = [
  { key: "common.hauptfaelligkeit", label: "Hauptfälligkeitsdatum" },
  { key: "common.naechste_faelligkeit", label: "Nächste Fälligkeit" },
  { key: "common.jahrespraemie_brutto", label: "Jahresprämie (Brutto)" },
  { key: "common.jahrespraemie_netto", label: "Jahresprämie (Netto)" },
  { key: "common.versicherungssteuersatz", label: "Versicherungssteuersatz" },
  { key: "common.praemie_je_zahlweise_brutto", label: "Prämie je Zahlweise (Brutto)" },
  { key: "common.abweichender_rechnungsempfaenger", label: "Abweichender Rechnungsempfänger" },
];

const resolveKlasse = (chars: Record<string, string>): KlasseKey | null => {
  const v = chars.vehicleClass ?? "";
  if (!v) return null;
  for (const m of VEHICLE_CLASS_TO_KEY) {
    if (v.includes(m.contains)) return m.key;
  }
  return null;
};

const formatValue = (v: string | undefined): { text: string; muted: boolean } =>
  !v ? { text: "—", muted: true } : { text: v, muted: false };

type Props = {
  sectionKey: "summen" | "vereinbarung" | "umfang" | "beitraege";
  tarifierung: TarifierungKey | "";
  vereinbarung: Vereinbarung | null;
  /** Aus `kfz.sammelgrid_json` deserialisierte Klassen-Definition. */
  sammelGrid: SammelGridState;
  risks: RiskSummary[];
  /** Aus `kfz.werte_json` deserialisiert: riskId → Wert-Map */
  werteByRisk: Record<number, Record<string, string>>;
  /** Aus `kfz.umfang_assignment_json` deserialisiert. */
  umfangAssignment: Record<number, string>;
  /** Aus `kfz.zahlung_json` deserialisiert: key → value. */
  zahlung: Record<string, string>;
};

const SammelOnlyHint = ({ what }: { what: string }) => {
  const styles = useStyles();
  return (
    <div className={styles.empty}>{what} — nur für Rahmenvertrag Sammelvertrag verfügbar.</div>
  );
};

const KfzReadOnlyTables = ({
  sectionKey,
  tarifierung,
  vereinbarung,
  sammelGrid,
  risks,
  werteByRisk,
  umfangAssignment,
  zahlung,
}: Props) => {
  const styles = useStyles();

  if (sectionKey === "summen") {
    if (risks.length === 0) {
      return <div className={styles.empty}>Keine Risiken vorhanden.</div>;
    }
    return (
      <div className={styles.werteRoot}>
        {risks.map((r) => {
          const plate = r.characteristics.licensePlate || `Risiko ${r.id}`;
          const edits = werteByRisk[r.id] ?? {};
          const filled = WERT_FIELD_LABELS.map((f) => ({
            ...f,
            value: edits[f.key] ?? r.characteristics[f.key] ?? "",
          })).filter((f) => f.value);
          return (
            <div key={r.id} className={styles.werteCard}>
              <h4 className={styles.werteCardTitle}>{plate}</h4>
              {filled.length === 0 ? (
                <span className={styles.empty}>
                  Noch keine Wert-Angaben für dieses Risiko.
                </span>
              ) : (
                <div className={styles.kvList}>
                  {filled.map((f) => (
                    <div key={f.key} className={styles.kvRow}>
                      <span className={styles.kvKey}>{f.label}</span>
                      <span className={styles.kvVal}>{f.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  if (sectionKey === "vereinbarung") {
    if (!vereinbarung) {
      return (
        <div className={styles.empty}>
          Keine Vereinbarung für die aktuelle Produkt-/Tarifierungsart hinterlegt.
        </div>
      );
    }
    return <VereinbarungTable vereinbarung={vereinbarung} />;
  }

  if (sectionKey === "umfang") {
    if (tarifierung !== "rahmenSammel") {
      return <SammelOnlyHint what="Versicherungsumfang-Matrix" />;
    }
    if (!vereinbarung) {
      return (
        <div className={styles.empty}>
          Ohne Versicherer-Vereinbarung kann keine Umfangs-Zuordnung dargestellt werden.
        </div>
      );
    }
    const klasseByRisk: Record<number, KlasseKey | null> = {};
    for (const r of risks) klasseByRisk[r.id] = resolveKlasse(r.characteristics);
    return (
      <UmfangAssignmentTable
        klassenDefinition={sammelGrid}
        vehicles={risks}
        klasseByRisk={klasseByRisk}
        assignment={umfangAssignment}
        onChange={() => {}}
        readOnly
      />
    );
  }

  if (sectionKey === "beitraege") {
    const klasseByRisk: Record<number, KlasseKey | null> = {};
    for (const r of risks) klasseByRisk[r.id] = resolveKlasse(r.characteristics);
    const showSummary = tarifierung === "rahmenSammel" && vereinbarung;
    const filledZahlung = ZAHLUNG_FIELD_LABELS.map((f) => ({
      ...f,
      value: zahlung[f.key] ?? "",
    }));
    return (
      <div style={{ display: "flex", flexDirection: "column", rowGap: 14 }}>
        {showSummary && (
          <SammelvertragZahlungSummary
            vereinbarung={vereinbarung}
            vehicles={risks}
            klasseByRisk={klasseByRisk}
            assignment={umfangAssignment}
            versicherungssteuersatz={sammelGrid.versicherungssteuersatz}
          />
        )}
        <div className={styles.kvList}>
          {filledZahlung.map((f) => {
            const { text, muted } = formatValue(f.value);
            return (
              <div key={f.key} className={styles.kvRow}>
                <span className={styles.kvKey}>{f.label}</span>
                <span
                  className={mergeClasses(styles.kvVal, muted && styles.kvValMuted)}
                >
                  {text}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return null;
};

export default KfzReadOnlyTables;
